import { useMemo } from 'react';
import { TrendingUp, Wrench, Building2, Layers, LayoutDashboard } from 'lucide-react';
import {
  type WorkspaceId,
  type TenantContext,
  WORKSPACES,
  resolveWorkspaceRouter,
  setLastUsedWorkspace,
} from '../utils/workspaceRouter';
import { getTenantFromHostname } from '../utils/tenantDetection';

/**
 * The workspace switcher: Sell · Deliver · Firm, one segmented control in the
 * top bar, visible to every firm user (ROLE_VISIBILITY_RECOMMENDATION.md
 * §3.1). Three visible choices beat a dropdown: the person sees where they
 * are and where else they can go without opening anything.
 */

const WORKSPACE_ICONS: Record<WorkspaceId, JSX.Element> = {
  sell: <TrendingUp className="size-4" aria-hidden="true" />,
  deliver: <Wrench className="size-4" aria-hidden="true" />,
  firm: <Building2 className="size-4" aria-hidden="true" />,
  'control-center': <LayoutDashboard className="size-4" aria-hidden="true" />,
  work: <Layers className="size-4" aria-hidden="true" />,
};

interface WorkspaceSwitcherProps {
  currentUser: any;
  engagements?: any[];
  activeWorkspace: WorkspaceId;
  onWorkspaceChange: (workspace: WorkspaceId) => void;
  /** Override auto-detected tenant context (e.g. force 'client' for client dashboards) */
  tenantContext?: TenantContext;
}

export function WorkspaceSwitcher({ currentUser, engagements, activeWorkspace, onWorkspaceChange, tenantContext }: WorkspaceSwitcherProps) {
  const tenant = useMemo(() => getTenantFromHostname(), []);
  const routerResult = useMemo(
    () => resolveWorkspaceRouter(currentUser, engagements, tenant, tenantContext),
    [currentUser, engagements, tenant, tenantContext],
  );
  const { accessibleWorkspaces, showSwitcher } = routerResult;

  if (!showSwitcher || accessibleWorkspaces.length < 2) return null;

  const choose = (ws: WorkspaceId) => {
    if (ws === activeWorkspace) return;
    setLastUsedWorkspace(tenant, ws);
    onWorkspaceChange(ws);
  };

  return (
    <div role="group" aria-label="Workspace" data-testid="workspace-switcher" className="flex items-center rounded-lg border border-slate-200 bg-slate-50 p-0.5">
      {accessibleWorkspaces.map((wsId) => {
        const ws = WORKSPACES[wsId];
        const active = wsId === activeWorkspace;
        return (
          <button
            key={wsId}
            type="button"
            aria-pressed={active}
            aria-label={ws.label}
            title={ws.description}
            onClick={() => choose(wsId)}
            className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 transition-colors ${active ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
          >
            {WORKSPACE_ICONS[wsId]}
            <span className="hidden sm:inline">{ws.label}</span>
          </button>
        );
      })}
    </div>
  );
}
